import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import {
    loadContractDetailsForContractId,
    loadServiceDetailsForContractId
} from '../../../store/directusService';
import { loadContractConstants } from '../../../store/participantService';
import { EDIT_ON_CLOSED_ROLES } from '../../../utils/formValidation/commonConstants';
import { hasRole } from '../../../utils/userRoles';
import { conductAppointmentRoles, group1 } from '../utils/calendarUtils';

import AttendanceRow from './AttendanceRow';

import styles from '../styles/startAppointmentButton.module.css';

const StartAppointmentRow = ({
    onPanelClose,
    disabled,
    isUpdateDisabled = false,
    hasParticipantAccess = true,
    appointment,
    participant,
    isAuditable = false,
    onSearchAuditItems
}) => {
    // HOOKS
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    // LOCAL STATE
    const [attendanceStatus, setAttendanceStatus] = useState('Not known');
    const [appointmentAttendanceId, setAppointmentAttendanceId] = useState('');
    const [isPONumberRequired, setIsPONumberRequired] = useState(false);
    const [isNavPTRequestSent, setIsNavPTRequestSent] = useState(false);
    const [isNavStartRequestSent, setIsNavStartRequestSent] = useState(false);
    const [isNavUpdateRequestSent, setIsNavUpdateRequestSent] = useState(false);
    const [isWarning, setIsWarning] = useState(false);
    const [hasGroup1Role, setHasGroup1Role] = useState(false);
    const [hasEditOnClosedRole, setHasEditOnClosedRole] = useState(false);
    const [canConductAppointment, setCanConductAppointment] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const contractDetailsById = useSelector(
        (state) => state.entities.directusService.contractDetailsById
    );
    const servicesByContractId = useSelector(
        (state) => state.entities.directusService.servicesByContractId
    );
    const contractConstants = useSelector(
        (state) => state.entities.participantService.contractConstants
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );

    // USE EFFECTS
    useEffect(() => {
        setHasGroup1Role(hasRole(group1, roles));
        setHasEditOnClosedRole(hasRole(EDIT_ON_CLOSED_ROLES, roles));
        setCanConductAppointment(hasRole(conductAppointmentRoles, roles) && hasParticipantAccess);
    }, [roles, hasParticipantAccess]);

    useEffect(() => {
        if (!appointment || !('attendances' in appointment) || !participant) return;
        if (!contractConstants || contractConstants.contractId !== participant.contractId)
            participant?.contractId && dispatch(loadContractConstants(participant.contractId));
        const attendanceRecord = appointment.attendances.find(
            (el) => el.participantId === participant.id
        );
        if (!attendanceRecord) return;
        setAppointmentAttendanceId(attendanceRecord?.id);
        setAttendanceStatus(attendanceRecord?.status || 'Not known');
    }, [appointment, participant]);

    useEffect(() => {
        if (!contractConstants || contractConstants.contractId !== participant?.contractId) return;
        setIsPONumberRequired(contractConstants.requirePoNumberBeforeAppointmentInitialAttended);
    }, [contractConstants, participant]);

    useEffect(() => {
        if (
            currentParticipant?.ptCode === participant.ptCode &&
            contractDetailsById[currentParticipant.contractId] &&
            servicesByContractId[currentParticipant.contractId]
        ) {
            if (isNavPTRequestSent) onPTNav();
            if (isNavStartRequestSent) onAppointmentNav('start');
            if (isNavUpdateRequestSent) onAppointmentNav('update');
        }
    }, [
        isNavPTRequestSent,
        isNavStartRequestSent,
        isNavUpdateRequestSent,
        contractDetailsById,
        servicesByContractId,
        currentParticipant
    ]);

    // FNS
    const onAppointmentNav = (action) => {
        onPanelClose();
        navigate('/appointments', {
            state: {
                appointment,
                appointmentAttendanceId,
                showWarning: isWarning,
                returnPath: location.pathname,
                action,
                participantId: participant?.id
            }
        });
    };

    const onPTNav = () => {
        onPanelClose();
        navigate('/edit_participant');
    };

    const onAction = () => {
        if (!(participant.contractId in contractDetailsById)) {
            dispatch(loadContractDetailsForContractId(participant.contractId));
        }
        if (!(participant.contractId in servicesByContractId)) {
            dispatch(loadServiceDetailsForContractId(participant.contractId));
        }
    };

    // EVENT HANDLERS
    const onStartAppointment = async (showWarning) => {
        if (disabled || !appointmentAttendanceId) return;
        setIsNavStartRequestSent(true);
        setIsWarning(showWarning);
        await onAction();
    };

    const onUpdateAppointment = async (showWarning) => {
        if (isUpdateDisabled || !appointmentAttendanceId) return;
        setIsNavUpdateRequestSent(true);
        setIsWarning(showWarning);
        await onAction();
    };

    const onNav = async () => {
        setIsNavPTRequestSent(true);
        await onAction();
    };

    // RENDER
    return participant && Object.keys(participant).length < 1 ? null : (
        <div
            className={`${styles.startAppointmentButton} ${styles.showAppointments}`}
            data-testid="startAppointmentRow">
            <AttendanceRow
                isDisabled={
                    disabled ||
                    (attendanceStatus && attendanceStatus !== 'Not known') ||
                    !appointmentAttendanceId
                }
                isUpdateDisabled={isUpdateDisabled}
                hasParticipantAccess={hasParticipantAccess}
                hasGroup1Role={hasGroup1Role}
                hasEditOnClosedRole={hasEditOnClosedRole}
                canConductAppointment={canConductAppointment}
                isAuditable={isAuditable}
                onSearchAuditItems={() =>
                    onSearchAuditItems(
                        'attendance',
                        `${participant?.firstName || ''} ${participant?.lastName || ''} - Attendance`
                    )
                }
                attendanceStatus={attendanceStatus}
                participant={participant}
                appointmentType={appointment?.type.toLowerCase()}
                isPONumberRequired={isPONumberRequired}
                onNav={onNav}
                onStartAppointment={onStartAppointment}
                onUpdateAppointment={onUpdateAppointment}
            />
        </div>
    );
};

StartAppointmentRow.propTypes = {
    onPanelClose: PropTypes.func,
    disabled: PropTypes.bool,
    isUpdateDisabled: PropTypes.bool,
    hasParticipantAccess: PropTypes.bool,
    appointment: PropTypes.object,
    participant: PropTypes.object,
    isAuditable: PropTypes.bool,
    onSearchAuditItems: PropTypes.func
};

export default StartAppointmentRow;
